/**
 * receiptScanAccess — geteilte Zugriffslogik für den Belegscanner (M13a).
 *
 * Single source of truth dafür, WER den Belegscanner sehen und benutzen darf.
 * Wird von `MainLayout` (Nav-Eintrag), `App` (Route-Registrierung) und
 * `ReceiptScanPage` (Inhalts-Gate) gemeinsam genutzt — kein Copy-Paste der
 * Flag-/Allowlist-Bedingung.
 *
 * Zwei Freischalt-Pfade (fail-closed — Default ist KEIN Zugriff):
 *   1. Beta-Allowlist: explizit gelistete Test-Account-UIDs sehen den Scanner
 *      LIVE, unabhängig vom Tier und vom Env-Flag (kontrollierte Erprobung).
 *   2. Allgemeine Freigabe: `REACT_APP_RECEIPT_SCAN_ENABLED='true'` UND Ultra-Tier
 *      (Pricing-Repackaging PR-T4 — Belegscanner ist ein Ultra-Differenzierer,
 *      architecture-pricing-repackaging.md §1/§3.2; inert, solange das Flag live
 *      `false`/unset ist ⇒ nur die Beta-Allowlist greift, byte-identisch zum IST).
 *
 * @module receiptScanAccess
 */

import type { Tier } from './entitlements';

/**
 * Beta-Erprobung Belegscanner — entfernen/erweitern, wenn allgemein freigegeben.
 *
 * Nur diese UIDs erhalten Zugriff unabhängig vom Premium-Status und vom Env-Flag.
 * HARTKODIERT für die kontrollierte Live-Erprobung; vor allgemeiner Freigabe
 * (Flag = 'true' für alle Premium-Nutzer) wieder leeren.
 */
export const RECEIPT_SCAN_BETA_UIDS: readonly string[] = ['b3DRR1Cj63cRjdJg5VCEUpBL7hk1'];

/**
 * Allgemeines Feature-Flag. Fail-closed: nur bei explizitem `'true'` aktiv.
 * Modul-Konstante, einmalig beim Bundle-Load ausgewertet.
 */
export const RECEIPT_SCAN_FLAG: boolean = process.env.REACT_APP_RECEIPT_SCAN_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob ein Nutzer den Belegscanner sehen/benutzen darf.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined,
 *              solange noch nicht eingeloggt).
 * @param tier - effektiver Tier des Nutzers (`resolveTier(sellerData)` aus
 *               `entitlements.ts`, §3.2). Ersetzt den früheren `isPremium`-Term:
 *               der Belegscanner ist ab GA ein Ultra-Feature.
 * @returns `true`, wenn die UID auf der Beta-Allowlist steht ODER das Flag aktiv
 *          ist UND der Nutzer den Ultra-Tier hat. Sonst `false`.
 */
export function canUseReceiptScan(uid: string | null | undefined, tier: Tier): boolean {
  if (uid && RECEIPT_SCAN_BETA_UIDS.includes(uid)) {
    return true;
  }
  return RECEIPT_SCAN_FLAG && tier === 'ultra';
}

// ---------------------------------------------------------------------------
// AVV-Zustimmungs-Gate (M13a-GA) — fail-closed, Muster `bankReconcileAccess.ts`
// ---------------------------------------------------------------------------
//
// Sichtbarkeit ≠ Nutzbarkeit (§2): `canUseReceiptScan` (oben) gatet AUSSCHLIESSLICH
// die Sichtbarkeit (Nav/Route/Teaser) und bleibt UNVERÄNDERT. Die AVV-Zustimmung
// ist ein zusätzliches RUNTIME-Gate unmittelbar vor dem Scan — der Drittlandtransfer
// (Anthropic/USA) ist nur mit versions-passender, server-persistierter Zustimmung
// zulässig. Ohne sie sperrt das FE-Gate (Client-Guard) und der Server lehnt
// fail-closed ab (`avv_consent_required`).

/**
 * Aktuelle Belegscanner-AVV-Version, deren Zustimmung erfasst und geprüft wird.
 *
 * Wert = Versionskennung des ausgelieferten AVV-Dokuments
 * (`public/legal/avv-belegscanner-v1.md`, Feld „Versionskennung"). Bewusst NICHT
 * gleich dem Datei-Stamm (`avv-belegscanner-v1`) — die Kennung trägt das
 * Freigabe-Datum, das Dokument selbst gibt sie vor. Wird beim Consent
 * server-persistent auf `sellers/{uid}.receiptScanAvvAcceptedVersion` geschrieben;
 * das Gate erscheint erneut, sobald die gespeicherte Version davon abweicht.
 *
 * MUSS byte-identisch zur Server-Kopie `RECEIPT_SCAN_AVV_VERSION`
 * (`functions/receipts/src/receiptScanGate.ts`) sein — Versions-Bump immer in
 * BEIDEN Paketen in derselben Welle (alte Zustimmungen werden dann ungültig →
 * gewolltes Re-Consent).
 */
export const RECEIPT_SCAN_AVV_VERSION = 'belegscanner-avv-v1-2026-07';

/**
 * Öffentlicher Pfad des versionierten AVV-Dokuments (statisch unter `public/legal/`
 * ausgeliefert). Der Dateiname folgt dem Doc-Stamm `avv-belegscanner-v1`, NICHT der
 * (datierten) Versionskennung. Die Datei ist bereits vom Jurist freigegeben — der
 * Coder LIEST sie nur (Laufzeit-Fetch), erstellt/ändert sie nie. Fehlt sie / liefert
 * die SPA index.html aus, greift der fail-closed Draft/Missing-Guard.
 */
export const RECEIPT_SCAN_AVV_DOC_PATH = '/legal/avv-belegscanner-v1.md';

/**
 * Marker, an dem der Entwurfs-Zustand des AVV-Dokuments erkannt wird (identisch
 * zum Bank-/Inbound-/Advisor-Draft-Marker). Solange die Datei diesen Marker
 * enthält, bleibt der Scan fail-closed gesperrt.
 */
export const RECEIPT_SCAN_AVV_DRAFT_MARKER = 'ENTWURF';

/**
 * Entscheidet fail-closed, ob für die aktuelle AVV-Version eine gültige Zustimmung
 * vorliegt. Vergleicht die server-persistierte
 * `sellers/{uid}.receiptScanAvvAcceptedVersion` exakt gegen
 * {@link RECEIPT_SCAN_AVV_VERSION}. Fehlt das Feld (noch nie zugestimmt) oder trägt
 * es eine ältere Version, ist der Scan gesperrt.
 *
 * @param acceptedVersion Wert aus `sellerData.receiptScanAvvAcceptedVersion` (oder
 *                        null/undefined, solange nicht zugestimmt wurde).
 * @returns `true` nur bei exakter Versions-Übereinstimmung.
 */
export function hasReceiptScanAvvConsent(
  acceptedVersion: string | null | undefined,
): boolean {
  return acceptedVersion === RECEIPT_SCAN_AVV_VERSION;
}
